import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Printer, Receipt, RotateCcw } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatMoment, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Tabs } from '@/components/ui/primitives'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'
import { RefundDialog } from '@/pages/sales/RefundDialog'
import { ReceiptDialog } from '@/pages/checkout/ReceiptDialog'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { t } from '@/i18n'

interface SaleRow {
  id: number
  number: string
  totalUsd: number
  completedAtUtc: string
  status: 'Completed' | 'PartiallyRefunded' | 'FullyRefunded' | 'Voided'
  lineCount: number
  cashierName: string | null
  customerName: string | null
}

interface RefundRow {
  id: number
  number: string
  saleId: number
  saleNumber: string
  totalUsd: number
  reason: string
  method: string
  occurredAtUtc: string
  lineCount: number
  isReversed: boolean
  reversalReason: string | null
  reversedBy: string | null
}

const STATUS_TONE = {
  Completed: 'success',
  PartiallyRefunded: 'warn',
  FullyRefunded: 'warn',
  Voided: 'danger',
} as const

export function SalesHistoryPage() {
  const queryClient = useQueryClient()
  const can = useSession((state) => state.can)

  const [refunding, setRefunding] = useState<SaleRow | null>(null)
  const [voiding, setVoiding] = useState<SaleRow | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [voidError, setVoidError] = useState<string | null>(null)
  const [receiptId, setReceiptId] = useState<number | null>(null)
  const [tab, setTab] = useState<'sales' | 'refunds'>('sales')
  /** The refund being undone. */
  const [reversingRefund, setReversingRefund] = useState<RefundRow | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => apiClient.get<{ id: number }[]>('/api/registers'),
  })
  const registerId = registers.data?.[0]?.id ?? null

  // One filter state per tab. Sharing it would carry a receipt number typed on the sales tab into
  // the refunds tab, which then shows nothing and looks broken.
  const [saleFilters, setSaleFilters] = useState<ListFilterState>(NO_FILTERS)
  const [refundFilters, setRefundFilters] = useState<ListFilterState>(NO_FILTERS)

  const sales = usePagedList<SaleRow>(['sales'], '/api/sales', {
    from: saleFilters.from,
    to: saleFilters.to,
    search: saleFilters.search,
  })

  const refunds = usePagedList<RefundRow>(['refunds'], '/api/refunds', {
    from: refundFilters.from,
    to: refundFilters.to,
    search: refundFilters.search,
  })

  const voidSale = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/sales/${voiding!.id}/void`, {
        registerId,
        reason: voidReason.trim(),
        approvedByUserId: null,
      }),
    onSuccess: async () => {
      // The same set the refund dialog refreshes. A void can now pay cash out of the drawer the
      // sale went into rather than the voider's own, and can put an account balance back, so the
      // cash screen and the customer list are as stale after one as after a refund.
      for (const key of ['sales', 'refunds', 'products', 'stock', 'shift-expected', 'customers', 'reports']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      setVoiding(null)
      setVoidReason('')
    },
    onError: (caught) =>
      setVoidError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not void.'),
  })

  return (
    <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('Sales')}</h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          A refund returns goods at the price they sold for. A void says the sale should never
          have happened and reverses it whole.
        </p>
      </header>

      <div className="mb-4">
        <Tabs
          tabs={[
            { value: 'sales' as const, label: t('Sales') },
            { value: 'refunds' as const, label: t('Refunds'), count: refunds.total ?? undefined },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'sales' && (
        <ListFilters
          value={saleFilters}
          onChange={setSaleFilters}
          searchHint={t('Receipt number or customer')}
        />
      )}

      {tab === 'refunds' && (
        <ListFilters
          value={refundFilters}
          onChange={setRefundFilters}
          searchHint={t('Refund or receipt number')}
        />
      )}

      {tab === 'sales' && (sales.page.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title={t('No sales yet')}
          description={t('Completed sales appear here, where they can be reprinted, refunded or voided.')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {sales.page.map((sale) => (
            <li
              key={sale.id}
              className="surface-raised rounded-[var(--radius-card)] border p-3"
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="tabular font-medium">{sale.number}</span>
                    <Badge tone={STATUS_TONE[sale.status]}>{sale.status}</Badge>
                  </div>
                  <p className="truncate text-xs text-[var(--text-secondary)]">
                    {formatMoment(sale.completedAtUtc)} · {sale.lineCount} items
                    {sale.cashierName ? ` · ${sale.cashierName}` : ''}
                    {sale.customerName ? ` · ${sale.customerName}` : ''}
                  </p>
                </div>

                <span className="tabular font-semibold">{formatUsd(sale.totalUsd)}</span>

                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Reprint ${sale.number}`}
                    onClick={() => setReceiptId(sale.id)}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>

                  {can(Permission.Refund) && sale.status !== 'Voided' && sale.status !== 'FullyRefunded' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Refund ${sale.number}`}
                      onClick={() => setRefunding(sale)}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}

                  {can(Permission.VoidSale) && sale.status === 'Completed' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Void ${sale.number}`}
                      onClick={() => {
                        setVoiding(sale)
                        setVoidError(null)
                      }}
                    >
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ))}

      {tab === 'sales' && (
        <Pager list={sales} />
      )}

      {tab === 'refunds' &&
        (refunds.page.length === 0 ? (
          <EmptyState
            icon={RotateCcw}
            title={t('No refunds yet')}
            description={t('Refunds given back appear here. A refund keyed by mistake can be reversed, which is the only way to undo one.')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {refunds.page.map((refund) => (
              <li
                key={refund.id}
                className="surface-raised flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`tabular font-medium ${
                        refund.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                      }`}
                    >
                      {refund.number}
                    </span>
                    <Badge tone="neutral">{refund.method}</Badge>
                    {refund.isReversed && (
                      <ReversedBadge reason={refund.reversalReason} by={refund.reversedBy} />
                    )}
                  </div>
                  <p className="truncate text-xs text-[var(--text-secondary)]">
                    against {refund.saleNumber} ·{' '}
                    {formatMoment(refund.occurredAtUtc)} · {refund.lineCount} items ·{' '}
                    {refund.reason}
                  </p>
                </div>

                <span
                  className={`tabular font-semibold ${
                    refund.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                  }`}
                >
                  {formatUsd(refund.totalUsd)}
                </span>

                <ReverseButton
                  isReversed={refund.isReversed}
                  label={`Reverse ${refund.number}`}
                  onClick={() => setReversingRefund(refund)}
                />
              </li>
            ))}
          </ul>
        ))}

      {tab === 'refunds' && (
        <Pager list={refunds} />
      )}

      <ReverseDialog
        kind="refund"
        id={reversingRefund?.id ?? null}
        what={
          reversingRefund
            ? `${reversingRefund.number} against ${reversingRefund.saleNumber} — ${formatUsd(reversingRefund.totalUsd)}`
            : ''
        }
        onClose={() => setReversingRefund(null)}
        invalidate={['refunds', 'sales', 'products', 'stock', 'shift-expected', 'customers', 'reports']}
      />

      <RefundDialog
        saleId={refunding?.id ?? null}
        saleNumber={refunding?.number ?? null}
        registerId={registerId}
        onClose={() => setRefunding(null)}
      />

      <Modal
        open={voiding !== null}
        onClose={() => setVoiding(null)}
        title={`Void ${voiding?.number ?? ''}`}
        description={t('Stock goes back to the batches it came from and the cash comes out of today\'s drawer.')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoiding(null)}>
              {t('Cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => voidSale.mutate()}
              disabled={!voidReason.trim() || voidSale.isPending}
            >
              {voidSale.isPending ? 'Voiding…' : 'Void the sale'}
            </Button>
          </>
        }
      >
        <p className="mb-4 text-sm text-[var(--text-secondary)]">
          The sale is not deleted and its receipt number stays used — a void is recorded, not
          hidden.
        </p>
        <Input
          label={t('Reason')}
          value={voidReason}
          onChange={(event) => setVoidReason(event.target.value)}
          placeholder={t('Why should this sale not stand?')}
          required
        />
        {voidError && (
          <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            {voidError}
          </p>
        )}
      </Modal>

      <ReceiptDialog saleId={receiptId} onClose={() => setReceiptId(null)} />
    </div>
  )
}
